import React, { useContext } from "react";
import classnames from "classnames";
import { withStyles, createStyles, WithStyles, Theme } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel, faFilePdf } from "@fortawesome/free-solid-svg-icons";
import CircularProgress from "@material-ui/core/CircularProgress";
import jsPDF from "jspdf";
import "jspdf-autotable";
import ReactHTMLTableToExcel from "react-html-table-to-excel";
import red from "@material-ui/core/colors/red";
import green from "@material-ui/core/colors/green";
import { FormattedMessage, useIntl } from "react-intl";
import html2canvas from "html2canvas";

const styles = (theme: Theme) =>
  createStyles({
    root: {},
    exportButton: {
      border: "none",
      outline: "none",
      color: "rgba(0, 0, 0, 0.87)",
      padding: "6px 7px",
      fontSize: "0.6875rem",
      minWidth: "64px",
      boxSizing: "border-box",
      lineHeight: 1.75,
      fontWeight: 500,
      fontFamily: "Montserrat,-apple-systrem,BlinkMacSystemFont,Segoe UI,Roboto,Oxygen-Sans,Ubuntu,Cantarell,Helvetica Neue,Helvetica,Arial,sans-serif",
      borderRadius: "4px",
      textTransform: "uppercase",
      cursor: "pointer",
      backgroundColor: "transparent",
      transition:
        "background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
    },
    icon: {
      marginLeft: "6px",
      fontSize: "12px",
      color: green[500],
    },
    green: {
      color: green[500],
    },
    red: {
      color: red[500],
    },
    excelIcon: {
      paddingLeft: "5px",
    },
    exportPDFText: {
      paddingLeft: "5px",
    },
  });

type DashboardFilters = {
  dealer?: string;
  brand: string;
  date: string;
  forecast?: string;
  period?: string;
  benchmarks?: string[];
  financialMonth?: string;
};

export type ExportButtonsProps = {
  id:
    | "metric-table"
    | "group-table"
    | "custom-table"
    | "dashboard-metrics"
    | "group-metrics"
    | "oem-metrics"
    | "custom-metrics"
    | "graph-table-dashboard"
    | "distribution-dashbord"
    | string;
  title: string;
  filters?: DashboardFilters;
} & React.HTMLAttributes<HTMLDivElement> &
  WithStyles<typeof styles>;

const ExportButtonsUnstlyed: React.SFC<ExportButtonsProps> = ({ classes, children, className, title, id, filters }) => {
  const [PDFLoading, setPDFLoading] = React.useState(false);
  const intl = useIntl();

  const ConvertPDF = async () => {
    setPDFLoading(true);
    try {
      if (id === "metric-table" || id === "group-table" || id === "custom-table" || id.includes("group-table-")) {
        const tableRows = document.getElementById(id).querySelectorAll("tr");
        const tableRowsArray = Array.from(tableRows);
        const tableArray = (tableRowsArray || []).map((r, i) => {
          if (id == "metric-table" && i > 1) {
            return Array.from(r.querySelectorAll("td"))
              .slice(1)
              .map(r => ({ content: r.textContent }));
          }
          if (id == "metric-table" && i > 0) {
            return Array.from(r.querySelectorAll("th"))
              .slice(1)
              .map(r => ({ content: r.textContent }));
          }
          if (id != "metric-table" && i > 0) {
            return Array.from(r.querySelectorAll("td"))
              .slice(1)
              .map(r => ({ content: r.textContent }));
          }
          if (id != "metric-table" && i > 0) {
            return Array.from(r.querySelectorAll("td"))
              .slice(1)
              .map(r => ({ content: r.textContent }));
          }
          return Array.from(r.querySelectorAll("th"))
            .slice(1)
            .map(r => ({ content: r.textContent }));
        });

        const tableContent = (tableArray || []).map(row => (row || []).map((r, ri) => ({ ...r, styles: { halign: ri >= 1 ? "right" : "left" } })));
        const doc: any = new jsPDF();
        doc.autoTable({
          head: [tableContent[0]],
          body: (tableContent || []).splice(1, (tableContent || []).length),
          theme: "striped",
          tableWidth: "auto",
          headStyles: { fillColor: "#ededed", textColor: [85, 85, 85] },
          styles: { cellPadding: 1, fontSize: 5, cellWidth: "auto" },
          margin: { top: 1, left: 1, right: 1 },
        });
        doc.save(`${title}.pdf`);
      }
      if (
        id === "dashboard-metrics" ||
        id === "group-metrics" ||
        id === "oem-metrics" ||
        id === "custom-metrics" ||
        id === "graph-table-dashboard" ||
        id === "distribution-dashbord"
      ) {
        const headers = [
          {
            dealer: "Dealer",
            brand: "Brand",
            date: "Date",
            forecast: "Forecast",
            period: "Period",
            financialMonth: "Financial Month",
            benchmarks: "Benchmarks",
          },
        ];
        const dashboardFilters = [
          {
            dealer: id == "group-metrics" ? "" : filters.dealer,
            brand: filters.brand,
            date: filters.date,
            forecast: filters.forecast,
            period: filters.period,
            financialMonth: filters.period === "Financial Year" ? filters.financialMonth : "",
            benchmarks: filters.benchmarks.length === 1 ? filters.benchmarks[0] : `${filters.benchmarks[0]}, ${filters.benchmarks[1]}`,
          },
        ];

        const input = document.getElementById(id);
        html2canvas(input).then(canvas => {
          const imgData = canvas.toDataURL("image/png");
          const pdf = new jsPDF("landscape");
          pdf.addImage(imgData, "JPEG", 0, 10, pdf.internal.pageSize.width - 2, pdf.internal.pageSize.height - 12);
          (pdf as any).autoTable({
            head: headers,
            body: dashboardFilters || [],
            theme: "striped",
            tableWidth: "auto",
            headStyles: { fillColor: "#ededed", textColor: [85, 85, 85] },
            styles: { cellPadding: 1, fontSize: 5, cellWidth: "auto" },
            margin: { top: 1, left: 1, right: 1 },
          });
          pdf.save(`${title}.pdf`);
        });
      }
    } catch (e) {
      setPDFLoading(false);
      return e;
    }
    setPDFLoading(false);
  };

  return (
    <div className={classnames(classes.root, className)}>
      {(id === "metric-table" || id === "group-table" || id === "custom-table" || id.includes("group-table-")) && (
        <Button color={"default"} style={{ padding: 0 }}>
          <span className={classes.excelIcon}>
            <FontAwesomeIcon className={classnames(classes.green)} size={"lg"} icon={faFileExcel} />
          </span>
          <ReactHTMLTableToExcel
            id="test-table-xls-button"
            className={classes.exportButton}
            table={id}
            filename={title}
            sheet="tablexls"
            buttonText={`${intl.formatMessage({ id: "export.to.excel.button" })}`}
          />
        </Button>
      )}
      <Button
        style={{ fontSize: "11px" }}
        color={"default"}
        onClick={() => {
          ConvertPDF();
        }}
      >
        {PDFLoading ? (
          <CircularProgress color={"secondary"} size={20} />
        ) : (
          <span>
            <FontAwesomeIcon icon={faFilePdf} className={classnames(classes.red)} size={"lg"} />
            <span className={classes.exportPDFText}>
              <FormattedMessage id="export.to.pdf.button" />
            </span>
          </span>
        )}
      </Button>
    </div>
  );
};

export const ExportButtons = withStyles(styles)(ExportButtonsUnstlyed);
